/**
 * helpHubService.ts - Phase 3
 * 
 * Service layer for Help Hub integrations
 * MOCK_MODE enabled for frontend testing without backend
 */

// ============================================
// MOCK MODE - Set to false when backend ready
// ============================================
const MOCK_MODE = false;
const MOCK_DELAY = 300; // ms

// ============================================
// CONFIGURATION
// ============================================
// Detect if running on a pod (not management server) - pods use proxy path
export const HELP_HUB_CONFIG = {
  BRAND_COLOR: '#005fac',
  BRAND_NAME: 'DashboardFox',
  API_BASE: '/api/help',
  
LINKS: {
    knowledgeBase: 'https://help.dashboardfox.com',
    community: 'https://support.dashboardfox.com',
    roadmap: 'https://roadmap.dashboardfox.com/roadmap',
    ideas: 'https://roadmap.dashboardfox.com',
    changelog: 'https://roadmap.dashboardfox.com/announcements',
    statusPage: 'https://status.dashboardfox.com',
    ticketPortal: 'https://2.donedone.com/ticket-portal/10041/29632251-2208-40d2-94d7-06c7aa6f1922',
    videoChannel: 'https://www.youtube.com/@dashboardfox',
  },

  FRILL: {
    WIDGET_KEY: 'YOUR_FRILL_WIDGET_KEY',
    SSO_ENABLED: true,
  },

  BETTERMODE: {
    COMMUNITY_URL: 'https://support.dashboardfox.com',
    SSO_ENABLED: true,
  },

  GUEST_LIMITS: {
    chatMessagesPerHour: 5,
    ticketsPerDay: 2,
  },
};

// ============================================
// TYPES
// ============================================
export interface KBArticle {
  id: string;
  title: string;
  excerpt?: string;
  url: string;
  category?: string;
  views?: number;
}

export interface FAQ {
  question: string;
  answer: string;
}

export interface SystemStatus {
  status: 'operational' | 'degraded' | 'down';
  uptime: string;
  lastChecked: Date;
  resources?: SystemResource[];
}

export interface SystemResource {
  name: string;
  status: 'operational' | 'degraded' | 'down';
  uptime: number;
}

export interface FrillIdea {
  id: string;
  slug: string;
  title: string;
  description?: string;
  votes: number;
  status: 'under-review' | 'planned' | 'in-progress' | 'completed' | 'declined';
  comments: number;
  createdAt: Date;
}

export interface FrillRoadmapItem {
  id: string;
  slug: string;
  title: string;
  description?: string;
  status: 'planned' | 'in-progress' | 'completed';
  category?: string;
  eta?: string;
}

export interface FrillChangelog {
  id: string;
  slug: string;
  title: string;
  content?: string;
  type: 'feature' | 'improvement' | 'fix' | 'announcement';
  publishedAt: Date;
  isNew?: boolean;
}

export interface CommunityDiscussion {
  id: string;
  title: string;
  author: string;
  replies: number;
  createdAt: Date;
  url: string;
}

export interface Video {
  id: string;
  title: string;
  duration: string;
  thumbnail?: string;
  url: string;
  category?: string;
}

export interface Ticket {
  id: string;
  subject: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  createdAt: Date;
  updatedAt: Date;
}

export interface TicketSubmission {
  category?: string;
  subject: string;
  message: string;
  email: string;
  userName?: string;
userTier?: string;
  metadata?: Record<string, any>;
  attachments?: { data: string; filename: string }[];
}

export interface ChatRequest {
  message: string;
  conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
  sessionId?: string;
}

export interface ChatResponse {
  text: string;
  sessionId?: string;
  sources?: Array<{ title: string; url: string }>;
}

// ============================================
// MOCK DATA
// ============================================
const MOCK_DATA = {
  status: {
    status: 'operational' as const,
    uptime: '99.98%',
    lastChecked: new Date(),
    resources: [
      { name: 'API', status: 'operational' as const, uptime: 99.99 },
      { name: 'Web App', status: 'operational' as const, uptime: 99.98 },
      { name: 'Database', status: 'operational' as const, uptime: 99.97 },
    ],
  },
  faqs: [
    { question: 'How do I connect a data source?', answer: 'Go to Settings → Data Sources → Add New. We support SQL Server, MySQL, PostgreSQL, and more.' },
    { question: 'How do I reset my password?', answer: 'Click your profile icon → Account Settings → Security → Change Password.' },
    { question: 'Can I export dashboards to PDF?', answer: 'Yes! Click the export icon on any dashboard and select PDF format.' },
  ],
  articles: [
    { id: '1', title: 'Getting Started Guide', excerpt: 'Learn the basics of DashboardFox...', url: 'https://help.dashboardfox.com/getting-started', category: 'Basics', views: 2400 },
    { id: '2', title: 'Connecting Data Sources', excerpt: 'How to connect SQL Server, MySQL...', url: 'https://help.dashboardfox.com/data-sources', category: 'Data Sources', views: 1800 },
    { id: '3', title: 'Building Your First Dashboard', excerpt: 'Step-by-step dashboard creation...', url: 'https://help.dashboardfox.com/first-dashboard', category: 'Dashboards', views: 1500 },
  ],
  ideas: [
    { id: '1', slug: 'dark-mode-support', title: 'Dark mode support', description: 'Add dark mode to reduce eye strain', votes: 142, status: 'planned' as const, comments: 23, createdAt: new Date('2025-12-01') },
    { id: '2', slug: 'slack-integration', title: 'Slack integration', description: 'Send dashboard alerts to Slack', votes: 98, status: 'in-progress' as const, comments: 15, createdAt: new Date('2025-11-15') },
    { id: '3', slug: 'mobile-app', title: 'Mobile app', description: 'Native iOS/Android app', votes: 87, status: 'under-review' as const, comments: 31, createdAt: new Date('2025-10-20') },
  ],
  roadmap: {
    'In Progress': [
      { id: '1', slug: 'slack-integration', title: 'Slack Integration', category: 'Integrations', status: 'in-progress' as const },
      { id: '2', slug: 'enhanced-pdf-export', title: 'Enhanced PDF Export', category: 'Features', status: 'in-progress' as const },
    ],
    'Planned': [
      { id: '3', slug: 'dark-mode', title: 'Dark Mode', category: 'UI', status: 'planned' as const, eta: 'Q2 2026' },
      { id: '4', slug: 'api-v2', title: 'API v2', category: 'Platform', status: 'planned' as const, eta: 'Q3 2026' },
    ],
    'Released': [
      { id: '5', slug: 'sso-support', title: 'SSO Support', category: 'Security', status: 'completed' as const },
      { id: '6', slug: 'custom-domains', title: 'Custom Domains', category: 'Platform', status: 'completed' as const },
    ],
  },
  changelog: [
    { id: '1', slug: 'dashboard-templates', title: 'New: Dashboard Templates', content: 'Choose from 20+ pre-built templates...', type: 'feature' as const, publishedAt: new Date('2026-01-28'), isNew: true },
    { id: '2', slug: 'chart-performance', title: 'Improved: Chart Performance', content: 'Charts now load 40% faster...', type: 'improvement' as const, publishedAt: new Date('2026-01-25'), isNew: true },
    { id: '3', slug: 'export-timezone-bug', title: 'Fixed: Export Timezone Bug', content: 'PDF exports now use correct timezone...', type: 'fix' as const, publishedAt: new Date('2026-01-20'), isNew: false },
  ],
  discussions: [
    { id: '1', title: 'Best practices for large datasets?', author: 'Mike T.', replies: 12, createdAt: new Date('2026-01-30'), url: 'https://support.dashboardfox.com/post/1' },
    { id: '2', title: 'How to schedule automated reports?', author: 'Sarah K.', replies: 8, createdAt: new Date('2026-01-29'), url: 'https://support.dashboardfox.com/post/2' },
    { id: '3', title: 'Custom CSS for embedded dashboards', author: 'Dev Team', replies: 15, createdAt: new Date('2026-01-28'), url: 'https://support.dashboardfox.com/post/3' },
  ],
  videos: [
    { id: '1', title: 'Quick Start in 5 Minutes', duration: '5:23', url: 'https://www.youtube.com/watch?v=example1', category: 'Getting Started' },
    { id: '2', title: 'Advanced Dashboard Design', duration: '12:45', url: 'https://www.youtube.com/watch?v=example2', category: 'Advanced' },
    { id: '3', title: 'Data Source Deep Dive', duration: '8:30', url: 'https://www.youtube.com/watch?v=example3', category: 'Data Sources' },
  ],
  tickets: [
    { id: 'TKT-1234', subject: 'Dashboard export not working', status: 'open' as const, createdAt: new Date('2026-01-31'), updatedAt: new Date('2026-01-31') },
    { id: 'TKT-1230', subject: 'Question about API limits', status: 'resolved' as const, createdAt: new Date('2026-01-28'), updatedAt: new Date('2026-01-29') },
  ],
};

// ============================================
// HELPER
// ============================================
const delay = (ms: number) => new Promise(r => setTimeout(r, ms));



// ============================================
// API CLIENT
// ============================================
class HelpHubService {
  private baseUrl: string;
  private guestChatCount: number = 0;
  private guestChatResetTime: number = 0;

  constructor() {
    this.baseUrl = HELP_HUB_CONFIG.API_BASE;
  }

  private async fetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const accessToken = localStorage.getItem('accessToken');
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      headers: { 'Content-Type': 'application/json', ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }), ...options?.headers },
      ...options,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `API error: ${response.status}`);
    }
    return response.json();
  }

  // ============================================
  // SYSTEM STATUS
  // ============================================
  async getSystemStatus(): Promise<SystemStatus> {
    if (MOCK_MODE) {
      await delay(MOCK_DELAY);
      return MOCK_DATA.status;
    }
    try {
      return await this.fetch<SystemStatus>('/status');
    } catch {
      return { status: 'operational', uptime: '99.9%', lastChecked: new Date() };
    }
  }

  // ============================================
  // AI CHAT
  // ============================================
  async sendChatMessage(request: ChatRequest, isGuest: boolean = false): Promise<ChatResponse> {
    if (isGuest) {
      const now = Date.now();
      if (now > this.guestChatResetTime) {
        this.guestChatCount = 0;
        this.guestChatResetTime = now + 3600000;
      }
      if (this.guestChatCount >= HELP_HUB_CONFIG.GUEST_LIMITS.chatMessagesPerHour) {
        throw new Error('Chat limit reached. Please sign up for unlimited access.');
      }
      this.guestChatCount++;
    }

    if (MOCK_MODE) {
      await delay(800);
      return {
        text: `Thanks for your question about "${request.message.slice(0, 50)}..."\n\nTo help you with this, I'd recommend checking our documentation. You can connect data sources by going to Settings → Data Sources → Add New.\n\nWould you like more specific guidance?`,
        sessionId: 'mock-session-123',
        sources: [
          { title: 'Getting Started Guide', url: 'https://help.dashboardfox.com/getting-started' },
        ],
      };
    }

    return this.fetch<ChatResponse>('/chat', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // ============================================
  // KNOWLEDGE BASE
  // ============================================
  async searchArticles(query: string): Promise<KBArticle[]> {
    if (!query.trim()) return [];
    if (MOCK_MODE) {
      await delay(MOCK_DELAY);
      const q = query.toLowerCase();
      return MOCK_DATA.articles.filter(a => 
        a.title.toLowerCase().includes(q) || a.excerpt?.toLowerCase().includes(q)
      );
    }
    return this.fetch<KBArticle[]>(`/kb/search?q=${encodeURIComponent(query)}`);
  }

  async getPopularArticles(): Promise<KBArticle[]> {
    if (MOCK_MODE) {
      await delay(MOCK_DELAY);
      return MOCK_DATA.articles;
    }
    return this.fetch<KBArticle[]>('/kb/popular');
  }

  async getFAQs(): Promise<FAQ[]> {
    if (MOCK_MODE) {
      await delay(MOCK_DELAY);
      return MOCK_DATA.faqs;
    }
    try {
      return await this.fetch<FAQ[]>('/kb/faqs');
    } catch {
      return MOCK_DATA.faqs;
    }
  }

  // ============================================
  // FRILL
  // ============================================
  async getFrillToken(): Promise<{ token: string; expiresAt: Date }> {
    if (MOCK_MODE) {
      await delay(MOCK_DELAY);
      return { token: 'mock-frill-token', expiresAt: new Date(Date.now() + 3600000) };
    }
    return this.fetch('/frill/token');
  }

  async getIdeas(limit: number = 10): Promise<FrillIdea[]> {
    if (MOCK_MODE) {
      await delay(MOCK_DELAY);
      return MOCK_DATA.ideas.slice(0, limit);
    }
    return this.fetch<FrillIdea[]>(`/frill/ideas?limit=${limit}`);
  }

  async getRoadmap(): Promise<Record<string, FrillRoadmapItem[]>> {
    if (MOCK_MODE) {
      await delay(MOCK_DELAY);
      return MOCK_DATA.roadmap;
    }
    return this.fetch('/frill/roadmap');
  }

  async getChangelog(limit: number = 10): Promise<FrillChangelog[]> {
    if (MOCK_MODE) {
      await delay(MOCK_DELAY);
      return MOCK_DATA.changelog.slice(0, limit);
    }
    return this.fetch<FrillChangelog[]>(`/frill/changelog?limit=${limit}`);
  }

  async getUnreadChangelogCount(): Promise<number> {
    if (MOCK_MODE) {
      await delay(MOCK_DELAY);
      return MOCK_DATA.changelog.filter(c => c.isNew).length;
    }
    try {
      const result = await this.fetch<{ count: number }>('/frill/changelog/unread');
      return result.count;
    } catch {
      return 0;
    }
  }

getFrillEmbedUrl(section: 'ideas' | 'roadmap' | 'announcements', token?: string): string {
    const baseUrl = `https://feedback.dashboardfox.com/${section}`;
    return token ? `${baseUrl}?sso_token=${encodeURIComponent(token)}` : baseUrl;
  }

  getCommunitySSOUrl(token: string): string {
    const base = HELP_HUB_CONFIG.BETTERMODE.COMMUNITY_URL;
    return `${base}/api/auth/sso?jwt=${encodeURIComponent(token)}&redirect_uri=${encodeURIComponent('/')}`;
  }

  // ============================================
  // BETTERMODE
  // ============================================
  async getBetterModeToken(): Promise<{ token: string; expiresAt: Date }> {
    if (MOCK_MODE) {
      await delay(MOCK_DELAY);
      return { token: 'mock-bettermode-token', expiresAt: new Date(Date.now() + 60000) };
    }
    return this.fetch('/community/token');
  }

  async getCommunityDiscussions(limit: number = 5): Promise<CommunityDiscussion[]> {
    if (MOCK_MODE) {
      await delay(MOCK_DELAY);
      return MOCK_DATA.discussions.slice(0, limit);
    }
    return this.fetch<CommunityDiscussion[]>(`/community/discussions?limit=${limit}`);
  }

  getCommunityEmbedUrl(section: 'qa' | 'discussions', token?: string): string {
    const baseUrl = HELP_HUB_CONFIG.BETTERMODE.COMMUNITY_URL;
    const embedPath = section === 'qa' ? '/embed/space/qa' : '/embed/space/discussions';
    if (token) {
      const redirectUri = encodeURIComponent(`${embedPath}?readonly=false&header=true&composer=true`);
      return `${baseUrl}/api/auth/sso?jwt=${encodeURIComponent(token)}&redirect_uri=${redirectUri}`;
    }
    return `${baseUrl}${embedPath}?readonly=true&header=true&composer=false`;
  }

  // ============================================
  // VIDEOS
  // ============================================
  async getVideos(limit: number = 10): Promise<Video[]> {
    if (MOCK_MODE) {
      await delay(MOCK_DELAY);
      return MOCK_DATA.videos.slice(0, limit);
    }
    try {
        const data = await this.fetch<{ embedUrl: string; videos: Video[] }>(`/videos?limit=${limit}`);
        return data.videos;      
    } catch {
      return MOCK_DATA.videos;
    }
  }

  // ============================================
  // TICKETS
  // ============================================
  async getUserTickets(): Promise<Ticket[]> {
    if (MOCK_MODE) {
      await delay(MOCK_DELAY);
      return MOCK_DATA.tickets;
    }
    return this.fetch<Ticket[]>('/tickets');
  }

  async submitTicket(ticket: TicketSubmission): Promise<{ ticketId: string; success: boolean }> {
    if (MOCK_MODE) {
      await delay(500);
      return { ticketId: `TKT-${Date.now().toString().slice(-4)}`, success: true };
    }
    return this.fetch('/tickets', {
      method: 'POST',
      body: JSON.stringify(ticket),
    });
  }

  async getTicketDetails(ticketId: string): Promise<Ticket & { messages: any[] }> {
    if (MOCK_MODE) {
      await delay(MOCK_DELAY);
      const ticket = MOCK_DATA.tickets.find(t => t.id === ticketId) || MOCK_DATA.tickets[0];
      return { ...ticket, messages: [] };
    }
    return this.fetch(`/tickets/${ticketId}`);
  }

  // ============================================
  // HELPCRUNCH
  // ============================================
private helpCrunchReady = false;
  private helpCrunchLoading = false;
  private helpCrunchConsentGranted = false;

  async loadHelpCrunchSDK(): Promise<void> {
    if (MOCK_MODE) return;
if (this.helpCrunchReady) return;
    if (this.helpCrunchLoading) {
      // Already loading — wait for it to finish rather than returning immediately
      return new Promise<void>((resolve) => {
        const check = setInterval(() => {
          if (this.helpCrunchReady) {
            clearInterval(check);
            resolve();
          }
        }, 100);
        setTimeout(() => { clearInterval(check); resolve(); }, 10000);
      });
    }

// Gate behind functional consent.
    // onConsent fires immediately if already accepted, or when user accepts.
    // Never fires on deny — HelpCrunch never loads without consent.
    if ((window as any).concord?.consent?.onConsent && !this.helpCrunchConsentGranted) {
      return new Promise<void>((resolve) => {
        (window as any).concord.consent.onConsent('functional', () => {
          this.helpCrunchConsentGranted = true;
          this.loadHelpCrunchSDK().then(resolve).catch(resolve);
        });
      });
    }

    this.helpCrunchLoading = true;

    return new Promise((resolve, reject) => {
      const widgetSrc = 'https://embed.helpcrunch.com/sdk.js';
      const existingScript = document.querySelector(`script[src="${widgetSrc}"]`);

      // Set up settings FIRST
      (window as any).helpcrunchSettings = {
        organization: 'team5000fish',
        appId: '176afdb7-cbda-4018-bb43-f7d6d7ed6934',
      };

      // Set up stub if needed
      if (!(window as any).HelpCrunch) {
        (window as any).HelpCrunch = function() {
          (window as any).HelpCrunch.q.push(arguments);
        };
        (window as any).HelpCrunch.q = [];
      }

      // Load script if not exists
      if (!existingScript) {
        const script = document.createElement('script');
        script.async = true;
        script.src = widgetSrc;
        script.onerror = () => {
          this.helpCrunchLoading = false;
          reject(new Error('Failed to load HelpCrunch'));
        };
        (document.body || document.head).appendChild(script);
      }

      // Register onReady callback
      (window as any).HelpCrunch('onReady', () => {
        this.helpCrunchReady = true;
        this.helpCrunchLoading = false;
        resolve();
      });

      // Fallback polling
      let attempts = 0;
      const checkReady = setInterval(() => {
        attempts++;
        const isReady = (window as any).HelpCrunch && 
                        typeof (window as any).HelpCrunch === 'function' && 
                        (window as any).HelpCrunch.q === undefined;
        
        if (isReady && !this.helpCrunchReady) {
          this.helpCrunchReady = true;
          this.helpCrunchLoading = false;
          clearInterval(checkReady);
          resolve();
        } else if (attempts >= 100) {
          clearInterval(checkReady);
          this.helpCrunchLoading = false;
          reject(new Error('HelpCrunch timeout'));
        }
      }, 200);
    });
  }

  initHelpCrunch(user: { id?: string; name?: string; email?: string }): void {
    if (MOCK_MODE) return;
    if (!(window as any).HelpCrunch || (window as any).HelpCrunch.q !== undefined) return;
    
    if (user.email || user.name) {
      (window as any).HelpCrunch('updateUser', {
        email: user.email,
        name: user.name,
        user_id: user.id,
      });
    }
  }

  openHelpCrunch(user?: { id?: string; name?: string; email?: string }): void {
    if (MOCK_MODE) { console.log('[MOCK] HelpCrunch opened'); return; }
    if (!(window as any).HelpCrunch || (window as any).HelpCrunch.q !== undefined) {
      console.error('HelpCrunch not ready');
      return;
    }
    
    if (user) this.initHelpCrunch(user);
    
    (window as any).HelpCrunch('showChatWidget');
    setTimeout(() => {
      (window as any).HelpCrunch('openChat');
    }, 100);
  }

  private isClosingHelpCrunch = false;
  private onCloseCallback: (() => void) | null = null;

  addHelpCrunchCloseBar(onClose: () => void): void {
    this.onCloseCallback = onClose;
    
    // Remove existing close bar if any
    const existing = document.getElementById('dfx-helpcrunch-close-bar');
    if (existing) existing.remove();
    
    const closeBar = document.createElement('div');
    closeBar.id = 'dfx-helpcrunch-close-bar';
    closeBar.style.cssText = `
      position: fixed !important;
      top: 0 !important;
      right: 0 !important;
      width: 450px !important;
      height: 60px !important;
      background: #005fac !important;
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      padding: 0 16px !important;
      z-index: 2147483647 !important;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2) !important;
      box-sizing: border-box !important;
    `;
    
    closeBar.innerHTML = `
      <span style="color: white; font-size: 15px; font-weight: 600;">Live Support</span>
      <button type="button" id="dfx-helpcrunch-close-btn" style="
        background: rgba(255,255,255,0.2);
        border: none;
        border-radius: 6px;
        color: white;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 14px;
        font-size: 13px;
        font-weight: 500;
      ">
        <svg viewBox="0 0 24 24" fill="currentColor" style="width:16px;height:16px;">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
        Close
      </button>
    `;
    
    const closeBtn = closeBar.querySelector('#dfx-helpcrunch-close-btn') as HTMLElement;
    closeBtn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.closeHelpCrunchAndReturn();
    };
    
    document.body.appendChild(closeBar);
  }

  closeHelpCrunchAndReturn(): void {
    if (this.isClosingHelpCrunch) return;
    this.isClosingHelpCrunch = true;
    
    const closeBar = document.getElementById('dfx-helpcrunch-close-bar');
    if (closeBar) {
      closeBar.style.transition = 'opacity 0.15s';
      closeBar.style.opacity = '0';
    }
    
    if ((window as any).HelpCrunch) {
      try {
        (window as any).HelpCrunch('closeChat');
        (window as any).HelpCrunch('hideChatWidget');
      } catch (e) {
        console.log('Error closing HelpCrunch:', e);
      }
    }
    
    setTimeout(() => {
      if (closeBar) closeBar.remove();
      if (this.onCloseCallback) this.onCloseCallback();
      this.isClosingHelpCrunch = false;
    }, 150);
  }

}

// Export singleton
const helpHubService = new HelpHubService();
export default helpHubService;

export const {
  getSystemStatus,
  sendChatMessage,
  searchArticles,
  getPopularArticles,
  getFAQs,
  getFrillToken,
  getIdeas,
  getRoadmap,
  getChangelog,
  getUnreadChangelogCount,
  getFrillEmbedUrl,
getBetterModeToken,
  getCommunityDiscussions,
  getCommunityEmbedUrl,
  getCommunitySSOUrl,
  getVideos,
  getUserTickets,
  submitTicket,
  getTicketDetails,
  loadHelpCrunchSDK,
  initHelpCrunch,
  openHelpCrunch,
  closeHelpCrunchAndReturn,
  addHelpCrunchCloseBar,
} = helpHubService;
